import { FC, useState, memo, useCallback, useEffect, useMemo } from 'react';
import styled from '@emotion/styled';
import {
  Spacing,
  Button,
  Typography,
  List,
  SearchField,
  Icon,
  StyledDiv,
  IconText,
  Dialog,
  JoyrideWalkthrough,
  IconButton,
  menuItems,
  StyledFixedButtonWrapper,
} from '@taleemabad/dsm/components';
import {
  GenericProfileV1,
  GenericProfile,
  UserProfile,
  ProfileStatus,
  SyncManagerStatus,
  SyncRecord,
  SyncRecordStatus,
  toPascalWithSpaceUniversal,
  userCache,
  analyticsService,
  EventNames,
} from '@taleemabad/db';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';
import { Capacitor } from '@capacitor/core';
import { toPascalCase, useJoyride, useSyncManager } from '@taleemabad/shared';
import { Step } from 'react-joyride';
import { useGlobalMenuClose } from '../../../hooks/useGlobalMenuClose';

// Interface definitions

const invitationStatusMap: Record<string, string> = {
  READ: 'Delivered',
  SENT: 'Delivered',
  FAILED: 'Failed',
  DELIVERED: 'Delivered',
};

// MC20-18296: a row can fail in two different ways and we render them
// distinctly so the user can tell which one happened:
//   - backend invitation_status=FAILED → SMS/WhatsApp delivery failed
//     (profile exists on the server, retrying the invite is meaningful)
//   - Dexie syncStatus=failed           → the profile record itself never
//     made it to the server, push-sync rejected it (different remedy)
const SYNC_FAILED_LABEL = 'Sync failed';

const getDisplayStatusLabel = (member: {
  syncStatus?: string;
  invitationStatus?: string;
}): string => {
  if (member.syncStatus === SyncRecordStatus.Failed) {
    return SYNC_FAILED_LABEL;
  }
  return invitationStatusMap[member.invitationStatus ?? ''] ?? 'Pending';
};
interface MemberProfile extends SyncRecord, GenericProfileV1 {
  backendProfileType?: string;
  displaySchoolOrGroup?: string;
}

// Extend existing types with additional properties used in this component
interface ActiveProfile extends GenericProfile {
  name?: string;
  userUsername?: string;
}

interface MembersListProps {
  members?: MemberProfile[];
  onAddedMember?: () => void;
  onMemberClick?: (id: GenericProfileV1) => void;
  onCancelInvite?: (profile: GenericProfileV1) => void;
  onWalkthroughNext?: () => void;
  loadingComponent?: () => React.ReactNode;
  menuItems?: menuItems[];
  onMenuItemClick?: (profileId: string) => void;
  onEditProfile?: (profileId: string) => void;
  onTransferProfile?: (profileId: string) => void;
  canTransferProfile?: (profileType: string) => boolean;
}

const memberListWalkthroughPhase = 'memberListWalkthrough';

const joyrideSteps: Step[] = [
  {
    target: '[data-tour="add-profiles-button"]',
    title: 'Add a Profile',
    content: 'You can add a profile by tapping the "Add Profiles" button',
    placement: 'top',
    disableBeacon: true,
  },
];

export const MemberList: FC<MembersListProps> = memo(
  ({
    members = [],
    onAddedMember,
    onMemberClick,
    onCancelInvite,
    onWalkthroughNext,
    loadingComponent,
    onTransferProfile,
    canTransferProfile,
  }) => {
    const [memberList, setMemberList] = useState<MemberProfile[]>([]);
    const [search, setSearch] = useState('');
    const [selectedProfile, setSelectedProfile] =
      useState<GenericProfileV1 | null>(null);
    const [showCancelInviteDialog, setShowCancelInviteDialog] = useState(false);
    const [openMenuId, setOpenMenuId] = useState<number | null>(null);

    const syncStatus = useSyncManager();

    const { run, stepIndex, tourCompleted, startTour, completeTour, nextStep } =
      useJoyride({
        localStorageKey: memberListWalkthroughPhase,
        steps: joyrideSteps,
        options: {
          continuous: true,
          showSkipButton: true,
          showProgress: false,
          disableOverlayClose: true,
          disableCloseOnEsc: false,
        },
      });

    useEffect(() => {
      setMemberList(members);
    }, [members]);

    // Single global listener for all menus - much more performant than N listeners
    const closeMenu = useCallback(() => setOpenMenuId(null), []);
    useGlobalMenuClose(openMenuId, closeMenu);

    // Enable walkthrough when member list is empty, sync is complete, and tour not completed
    useEffect(() => {
      if (
        memberList?.length === 0 &&
        !tourCompleted &&
        syncStatus === SyncManagerStatus.Synced
      ) {
        // Small delay to ensure DOM is ready
        const timer = setTimeout(() => {
          startTour();
        }, 500);
        return () => clearTimeout(timer);
      }
    }, [memberList.length, tourCompleted, startTour, syncStatus]);

    const filteredMembers = useMemo(() => {
      // First filter out invalid members (missing required fields)
      const validMembers = memberList.filter(
        (member) => member?.name && member?.username && member?.type,
      );

      const searchQuery = search.trim().toLowerCase();
      if (!searchQuery) return validMembers;

      return validMembers.filter((member) => {
        const nameMatch = member.name?.toLowerCase().includes(searchQuery);
        const typeMatch = member.type?.toLowerCase().includes(searchQuery);
        return Boolean(nameMatch || typeMatch);
      });
    }, [search, memberList]);

    const renderInvitationStatus = (statusLabel: string) => {
      const isInvited = statusLabel === 'Invited';
      const isFailureLabel =
        statusLabel === 'Failed' || statusLabel === SYNC_FAILED_LABEL;
      let textVariant:
        | 'textBodySmallSchemesSuccess'
        | 'textBodySmallSchemesError'
        | 'textBodySmall';
      if (isInvited) {
        textVariant = 'textBodySmallSchemesSuccess';
      } else if (isFailureLabel) {
        textVariant = 'textBodySmallSchemesError';
      } else {
        textVariant = 'textBodySmall';
      }
      let iconColor: string;
      if (isInvited) {
        iconColor = '--md-sys-color-on-success';
      } else if (isFailureLabel) {
        iconColor = '--md-sys-color-schemes-error';
      } else {
        iconColor = '--md-sys-color-on-surface-variant';
      }
      return (
        <IconText
          icon={isInvited ? 'check_small' : ''}
          text={statusLabel}
          textVariant={textVariant}
          iconColor={iconColor}
          data-cy={
            statusLabel === SYNC_FAILED_LABEL
              ? 'profile-sync-failed-pill'
              : 'profile-invitation-status-pill'
          }
        />
      );
    };

    const handleWalkthroughComplete = () => {
      if (onWalkthroughNext) {
        onWalkthroughNext();
      } else {
        onAddedMember?.();
      }
      completeTour();
    };

    const handleSendWhatsAppInvitation = useCallback(
      (member: MemberProfile) => {
        // Stop propagation to prevent triggering the parent List onClick
        // Note: Since Button onClick doesn't pass event, we'll handle this in the wrapper

        // Get current user's information
        const activeProfile =
          userCache.getActiveProfile() as ActiveProfile | null;
        const userProfile = userCache.getUserFromCache() as UserProfile | null;

        // Handle missing name key - try multiple sources
        let inviterName = ''; // Default fallback

        if (activeProfile?.name) {
          // First try: name from active profile
          inviterName = toPascalCase(activeProfile.name);
        } else if (userProfile?.name) {
          // Second try: name from user profile cache
          inviterName = toPascalCase(userProfile.name);
        } else if (activeProfile?.userUsername) {
          // Third try: username (phone number) from active profile
          inviterName = activeProfile.userUsername;
        }

        const inviterRole = activeProfile?.type
          ? toPascalWithSpaceUniversal(activeProfile.type)
          : 'Administrator';

        // Determine which video link to use based on member type
        const memberTypeLower = member.type?.toLowerCase();
        let videoLink = '';
        let videoLabel = '';

        if (memberTypeLower === 'principal' || memberTypeLower === 'teacher') {
          videoLink =
            'https://drive.google.com/file/d/1QpPX2FM8LcZTN4sOZsE3Dtm6hVMa4kXd/view?usp=drive_link';
          videoLabel = 'Video for Principal/Teacher';
        } else {
          videoLink =
            'https://drive.google.com/file/d/1bwP2cE1HGv2Bh0tZLFkS7E1aQFCDBkhv/view?usp=drive_link';
          videoLabel = 'Video for PD/RM/Coach';
        }

        const memberName = member?.name ? toPascalCase(member.name) : 'User';
        const memberRole = member?.type
          ? toPascalWithSpaceUniversal(member.type)
          : 'Member';

        const message = `Dear ${memberName},

You have been invited to join NIETE's official platform by ${inviterName} (${inviterRole}) for the role of ${memberRole}.

NIETE is an education technology initiative working to ensure that every child in Pakistan receives quality education — no matter where they live.

We empower teachers, school leaders, and education officers through tools that make reporting, monitoring, and academic support easier, faster, and more impactful.

Click the links below to install the app and log in:

Install App:
https://play.google.com/store/apps/details?id=pk.edu.niete&pcampaignid=web_share

Once logged in, please complete your profile by adding your information.

Together, let's make learning more meaningful and accessible for every child.

— NIETE Team


${videoLabel}:
${videoLink}`;

        const params = new URLSearchParams({ text: message });
        window.open(`https://wa.me/?${params.toString()}`);
      },
      [],
    );

    const handleMenuToggle = useCallback(
      (member: MemberProfile, newOpenMenuId: number | null) => {
        if (newOpenMenuId === member.id) {
          analyticsService.trackEvent(EventNames.erpTransferProfileMenuOpened, {
            toTransferProfileId: member.id,
            toTransferProfileType: member.type,
            profileId: userCache.getActiveProfile()?.id,
            profileType: userCache.getActiveProfile()?.type,
          });
        }
        setOpenMenuId(newOpenMenuId);
      },
      [],
    );

    const handleTransferClick = (profile: MemberProfile) => {
      setOpenMenuId(null);
      onTransferProfile?.(profile.uuid);
    };

    return (
      <>
        {memberList.length > 0 && (
          <SearchField
            placeholder="Search Teacher"
            variant="outlined"
            onChange={setSearch}
            onClearClick={() => setSearch('')}
            data-cy="profile-search-field"
          />
        )}
        <Spacing margin="md" />
        {memberList.length > 0 ? (
          <StyledMemberList marginBottom={78} data-cy="member-list-grid">
            {filteredMembers.length > 0 &&
              filteredMembers.map((member) => (
                <StyledListWrapper
                  key={member.uuid ?? member.id}
                  data-cy="member-list-item"
                >
                  {canTransferProfile?.(member.type) && (
                    <StyledMenuButtonWrapper data-menu-id={member.id}>
                      <StyledMenuActivator
                        aria-haspopup="true"
                        aria-expanded={openMenuId === member.id}
                        aria-controls={`profile-menu-${member.id}`}
                        data-cy="profile-menu-button"
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          const newOpenMenuId =
                            openMenuId === member.id ? null : member.id;
                          handleMenuToggle(member, newOpenMenuId);
                        }}
                        onKeyDown={(e: React.KeyboardEvent) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            const newOpenMenuId =
                              openMenuId === member.id ? null : member.id;
                            handleMenuToggle(member, newOpenMenuId);
                          }
                          if (e.key === 'Escape' && openMenuId === member.id) {
                            e.stopPropagation();
                            setOpenMenuId(null);
                          }
                        }}
                      >
                        <IconButton
                          icon="more_vert"
                          data-cy="profile-menu-icon"
                        />
                      </StyledMenuActivator>
                      {openMenuId === member.id && (
                        <StyledProfileMenu
                          id={`profile-menu-${member.id}`}
                          data-cy="profile-menu-dropdown"
                        >
                          {canTransferProfile?.(member.type) && (
                            <StyledProfileMenuItem
                              data-cy="transfer-profile-menu-item"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTransferClick(member);
                              }}
                            >
                              <Icon name="swap_horiz" size="regular" />
                              <Typography variant="textBodyLarge">
                                Transfer Profile
                              </Typography>
                            </StyledProfileMenuItem>
                          )}
                        </StyledProfileMenu>
                      )}
                    </StyledMenuButtonWrapper>
                  )}
                  <List
                    overlineText={toPascalWithSpaceUniversal(member.type)}
                    overlineTextVariant="textTitleSmall"
                    heading={member.name}
                    supportingText={member.username}
                    trailingComponent={renderInvitationStatus(
                      getDisplayStatusLabel(member),
                    )}
                    onClick={() => {
                      setSelectedProfile(member!);
                      onMemberClick?.(member);
                    }}
                    data-cy="profile-list-card"
                  >
                    {
                      <StyledDiv
                        flexDirection="column"
                        justifyContent="flex-end"
                        alignItem="flex-end"
                        padding="12px"
                        gap={12}
                      >
                        {member.invitationStatus !== ProfileStatus.Pending &&
                          Capacitor.isNativePlatform() && (
                            <StyledDiv
                              width="100%"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Button
                                iconColor="var(--md-sys-color-on-primary)"
                                variant="outlined"
                                width="100%"
                                onClick={() =>
                                  handleSendWhatsAppInvitation(member)
                                }
                                data-cy="share-whatsapp-button"
                              >
                                <StyledDiv
                                  gap={6}
                                  alignItem="center"
                                  justifyContent="center"
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                  >
                                    <g clip-path="url(#clip0_1106_4036)">
                                      <path
                                        d="M11.9971 0.666992H12.0029C13.7691 0.667146 15.5107 1.08012 17.0889 1.87305C18.6671 2.66598 20.0384 3.81644 21.0928 5.2334C22.1471 6.65038 22.855 8.2947 23.1611 10.0342C23.4672 11.7736 23.3627 13.5602 22.8555 15.252C22.3483 16.9436 21.4528 18.4933 20.2402 19.7773C19.0275 21.0614 17.5311 22.0443 15.8711 22.6475C14.211 23.2506 12.4328 23.4572 10.6787 23.251C8.92473 23.0443 7.24336 22.4315 5.76855 21.46L5.50195 21.2852L5.19824 21.3818L1.83887 22.4551L2.91699 19.2451L3.02539 18.9199L2.82422 18.6416C1.59827 16.9512 0.863535 14.9549 0.701172 12.873C0.5388 10.7911 0.955129 8.70484 1.9043 6.84473C2.85341 4.98472 4.29816 3.42327 6.0791 2.33301C7.86019 1.24274 9.90878 0.666223 11.9971 0.666992Z"
                                        stroke="#3A7950"
                                        stroke-width="1.33333"
                                      />
                                      <path
                                        d="M18.9854 16.9446C18.6959 17.7622 17.5477 18.4401 16.6305 18.6383C16.0035 18.7718 15.1845 18.8783 12.4274 17.7352C8.9008 16.2731 6.62991 12.6909 6.45299 12.4582C6.28241 12.2257 5.02793 10.5606 5.02793 8.83873C5.01235 8.29258 5.11377 7.74941 5.32539 7.24569C5.53701 6.74196 5.85393 6.28933 6.25488 5.91817C6.59304 5.62179 7.03262 5.46752 7.48183 5.48758C7.63023 5.48758 7.76377 5.49513 7.88391 5.50098C8.23652 5.51609 8.41343 5.53704 8.64591 6.09338C8.93541 6.7908 9.64038 8.51292 9.72446 8.68983C9.78383 8.78629 9.81941 8.89548 9.82827 9.0084C9.83712 9.12132 9.819 9.23472 9.77539 9.33925C9.67678 9.54269 9.54557 9.72864 9.38695 9.88974C9.21004 10.0937 9.0419 10.2497 8.86498 10.4687C8.70293 10.6593 8.51992 10.8633 8.72389 11.2156C9.2512 12.1129 9.90719 12.9281 10.6709 13.6352C11.4912 14.3859 12.446 14.9747 13.485 15.3707C13.6231 15.4362 13.7777 15.4585 13.9287 15.4347C14.0796 15.4109 14.2199 15.342 14.3311 15.2372C14.6656 14.8399 14.9785 14.4251 15.2685 13.9944C15.354 13.8517 15.4895 13.746 15.6486 13.6978C15.8078 13.6495 15.9792 13.6622 16.1295 13.7334C16.4536 13.846 18.1679 14.6935 18.5205 14.8689C18.8731 15.0444 19.1053 15.1299 19.1909 15.2783C19.2764 15.4267 19.2749 16.1266 18.9854 16.9446Z"
                                        fill="#3A7950"
                                      />
                                    </g>
                                    <defs>
                                      <clipPath id="clip0_1106_4036">
                                        <rect
                                          width="24"
                                          height="24"
                                          fill="white"
                                        />
                                      </clipPath>
                                    </defs>
                                  </svg>
                                  Share on WhatsApp
                                </StyledDiv>
                              </Button>
                            </StyledDiv>
                          )}
                        <Button
                          variant="deleted-text"
                          onClick={() => {
                            setSelectedProfile(member!);
                            setShowCancelInviteDialog(true);
                          }}
                          data-cy="cancel-invite-button"
                        >
                          Cancel Invite
                        </Button>
                      </StyledDiv>
                    }
                    {member.syncStatus === SyncRecordStatus.Failed && (
                      <StyledDiv
                        padding="-rk-spacing-md"
                        data-cy="profile-sync-error"
                      >
                        <Typography variant="textBodySmallSchemesError">
                          Profile Failed to Sync.
                        </Typography>
                      </StyledDiv>
                    )}
                  </List>
                </StyledListWrapper>
              ))}
          </StyledMemberList>
        ) : (
          syncStatus === SyncManagerStatus.Synced && (
            <NoContentWrapper data-cy="no-profiles-message">
              <Icon
                name="add_circle"
                size="xxxl"
                color="--md-sys-color-outline"
              />
              <Spacing margin="xl">
                <Typography variant="textTitleSmall">
                  No Profiles added
                </Typography>
              </Spacing>
              <Button
                data-tour="add-profiles-button"
                data-cy="add-profiles-button-empty"
                icon="add"
                onClick={onAddedMember}
              >
                Add Profiles
              </Button>
            </NoContentWrapper>
          )
        )}
        <StyledDiv marginBottom={40}>{loadingComponent?.()}</StyledDiv>

        {memberList.length > 0 && (
          <StyledFixedButtonWrapper justify="flex-end">
            <Button
              icon="add"
              onClick={onAddedMember}
              data-cy="add-profiles-button-fixed"
            >
              Add Profiles
            </Button>
          </StyledFixedButtonWrapper>
        )}
        <Dialog
          open={showCancelInviteDialog}
          onClose={() => setShowCancelInviteDialog(false)}
          data-cy="cancel-invite-dialog"
        >
          <StyledDiv flexDirection="column">
            <IconText
              icon="warning"
              text="Cancel Invite"
              iconColor="--md-sys-color-schemes-error"
              textVariant="textTitleLarge"
              data-cy="cancel-invite-dialog-title"
            />
            <Spacing margin="md" />
            <Typography
              variant="textBodyMedium"
              data-cy="cancel-invite-dialog-message"
            >
              Are you sure you want to cancel this invite?
            </Typography>
            <Spacing margin="md" />
            <Button
              variant="delete-filled"
              onClick={() => {
                onCancelInvite?.(selectedProfile!);
                setShowCancelInviteDialog(false);
              }}
              data-cy="cancel-invite-confirm-button"
            >
              Done
            </Button>
            <Spacing margin="md" />
            <Button
              variant="outlined"
              onClick={() => setShowCancelInviteDialog(false)}
              data-cy="cancel-invite-cancel-button"
            >
              Cancel
            </Button>
          </StyledDiv>
        </Dialog>

        <JoyrideWalkthrough
          run={run}
          steps={joyrideSteps}
          stepIndex={stepIndex}
          forceNextButton={true}
          onNext={nextStep}
          onSkip={completeTour}
          onClose={completeTour}
          onComplete={handleWalkthroughComplete}
        />
      </>
    );
  },
);

const NoContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  left: 272;
  right: 0;
  bottom: 0;
  margin: var(--rk-spacing-xl);
  width: calc(100% - 304px);

  ${mq(BreakpointSizes.xs)} {
    left: 0;
    width: unset;
  }
`;

const StyledListWrapper = styled.div`
  position: relative;
`;

const StyledMenuButtonWrapper = styled.div`
  position: absolute;
  top: var(--rk-spacing-md);
  right: var(--rk-spacing-md);
  z-index: 10;
`;

const StyledMenuActivator = styled.button`
  display: inline-flex;
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  &:focus {
    outline: 2px solid var(--md-sys-color-primary);
    outline-offset: 2px;
  }
`;

const StyledProfileMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  background: var(--md-sys-color-surface-container);
  border-radius: var(--md-sys-shape-corner-extra-small);
  box-shadow: var(--md-sys-elevation-level2);
  min-width: 180px;
  overflow: hidden;
  z-index: 1000;
`;

const StyledProfileMenuItem = styled.div`
  display: flex;
  align-items: center;
  gap: var(--rk-spacing-md);
  padding: var(--rk-spacing-md) var(--rk-spacing-lg);
  cursor: pointer;
  user-select: none;

  &:hover {
    background-color: var(--md-sys-color-surface-container-highest);
  }

  &:active {
    background-color: var(--md-sys-color-secondary-container);
  }
`;

const StyledMemberList = styled(StyledDiv)`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--rk-spacing-xl);
  ${mq(BreakpointSizes.xs)} {
    display: flex;
    flex-direction: column;
    gap: var(--rk-spacing-md);
  }
`;
